import { useState } from 'react';
import {
  useIngestStatus,
  useLiveStats,
  usePauseIngest,
  useStartIngest,
} from '../hooks/useApi';
import ProgressBar from '../components/ProgressBar';

function fmtTimestamp(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function isRunning(status?: string) {
  return status === 'pending' || status === 'running';
}

export default function IngestPage() {
  const [resourceId, setResourceId] = useState('4dbe5667-7b6b-41d7-82af-211562424d9a');
  const [totalRecords, setTotalRecords] = useState<number | ''>(3700000);
  const [rateRps, setRateRps] = useState<number | ''>(1);
  const [jobId, setJobId] = useState<string | null>(null);

  const start = useStartIngest();
  const pause = usePauseIngest();
  const { data: status, isFetching } = useIngestStatus(jobId);
  const { data: live } = useLiveStats();

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = resourceId.trim();
    if (!id) return;

    try {
      const res = await start.mutateAsync({
        resourceId: id,
        totalRecords: totalRecords === '' ? undefined : Number(totalRecords),
        rateRps: rateRps === '' ? undefined : Number(rateRps),
      });
      setJobId(res.jobId);
    } catch {
      // mutation error surfaces in start.error
    }
  };

  const handlePause = () => {
    if (jobId) pause.mutate(jobId);
  };

  const running = isRunning(status?.status);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-[var(--fg)]">
        Ingestion Control
      </h1>
      <p className="mt-1 text-[var(--muted)]">
        Start a new import and monitor progress live.
      </p>

      <form
        onSubmit={handleStart}
        className="mt-6 grid grid-cols-1 gap-3 rounded-xl border border-[var(--border)] bg-[var(--card)]/40 p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4"
      >
        <input
          type="text"
          value={resourceId}
          onChange={(e) => setResourceId(e.target.value)}
          placeholder="Resource ID"
          required
          className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--muted)] outline-none ring-[var(--ring)] transition focus:border-[var(--primary)] focus:ring-1"
        />
        <input
          type="number"
          min={1}
          value={totalRecords}
          onChange={(e) =>
            setTotalRecords(e.target.value === '' ? '' : Number(e.target.value))
          }
          placeholder="Total records (optional)"
          className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--muted)] outline-none ring-[var(--ring)] transition focus:border-[var(--primary)] focus:ring-1"
        />
        <input
          type="number"
          min={1}
          value={rateRps}
          onChange={(e) =>
            setRateRps(e.target.value === '' ? '' : Number(e.target.value))
          }
          placeholder="Rate (RPS, optional)"
          className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--muted)] outline-none ring-[var(--ring)] transition focus:border-[var(--primary)] focus:ring-1"
        />
        <button
          type="submit"
          disabled={start.isPending || !resourceId.trim()}
          className="rounded-lg bg-[var(--primary)] px-5 py-2 text-sm font-medium text-[var(--primary-fg)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {start.isPending ? 'Starting…' : 'Start Ingest'}
        </button>
      </form>

      {start.error && (
        <div className="mt-4 rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/10 px-4 py-3 text-sm text-[var(--danger)]">
          Start failed: {start.error.message}
        </div>
      )}

      {status && (
        <div className="mt-8 rounded-xl border border-[var(--border)] bg-[var(--card)]/40 p-6 shadow-sm">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-lg font-semibold text-[var(--fg)]">
                Job {status.jobId}
              </h2>
              <p className="text-sm text-[var(--muted)]">
                Status:{' '}
                <span className="font-medium text-[var(--fg)]">
                  {isFetching ? `${status.status} • syncing…` : status.status}
                </span>
                {live?.updatedAt && (
                  <span className="ml-2 text-xs">
                    (updated {fmtTimestamp(live.updatedAt)})
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={handlePause}
              disabled={pause.isPending || !running}
              className="rounded-lg border border-[var(--warning)]/50 bg-[var(--warning)]/10 px-5 py-2 text-sm font-medium text-[var(--warning)] transition hover:bg-[var(--warning)]/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pause.isPending ? 'Pausing…' : 'Pause Ingest'}
            </button>
          </div>

          <div className="mt-6">
            <ProgressBar
              value={status.fetchedRecords ?? 0}
              max={status.totalRecords || 1}
              label={`${status.percentComplete ?? 0}% complete`}
            />
          </div>

          {status.error && (
            <div className="mt-4 rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/10 px-4 py-3 text-sm text-[var(--danger)]">
              Error: {status.error}
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg)]/50 p-3">
              <p className="text-[var(--muted)]">Started</p>
              <p className="mt-1 font-medium text-[var(--fg)]">
                {fmtTimestamp(status.startedAt)}
              </p>
            </div>
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg)]/50 p-3">
              <p className="text-[var(--muted)]">Completed</p>
              <p className="mt-1 font-medium text-[var(--fg)]">
                {fmtTimestamp(status.completedAt)}
              </p>
            </div>
            <div className="rounded-lg border border-[var(--border)] bg-[var(--bg)]/50 p-3">
              <p className="text-[var(--muted)]">Last Offset</p>
              <p className="mt-1 font-medium text-[var(--fg)]">
                {(status.lastOffset ?? 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
